import type { NextPage } from "next";
import Image from "next/image";
import { ReactNode } from "react";
import { useMedia } from "react-use";
import SubPageLayout from "../../../components/common/layouts/SubPageLayout";
import LeftArrowSvg from "../../../components/common/ui/LeftArrowSvg";
import NormalAnimatedBtn from "../../../components/common/ui/NormalAnimatedBtn";
import { MOBILE_MAX_WIDTH } from "../../../constant";
import { useTranslation } from "../../../services/i18n";
import styles from "./index.module.scss";

const back = () => {
  if (window.history.state.idx > 0) window.history.back();
  else window.location.href = "/contents";
};

const RepairPlan: NextPage = () => {
  const t = useTranslation();
  const isMobile = useMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`, false);
  const alignsVertical = useMedia(`(max-width: 1270px)`, false);

  return (
    <SubPageLayout
      title="CONTENT"
      titleImgSrc="/images/text_images/content_vertical.svg"
      bgImgUrl="/images/bg-gallery-pc.jpg"
      spBgImgUrl="/images/bg-gallery-sp.jpg"
      isMobile={isMobile}
      extraContainerClass={
        isMobile
          ? "min-h-[calc(100vh-64px)] mt-[24px]"
          : "min-h-[calc(100vh-80px)]"
      }
    >
      {!alignsVertical && (
        <div className={styles.pageTitleArea}>
          <Image src="/images/title-mark.svg" width={44} height={44} alt="" />
          <h1>{t("repairProject")}</h1>
        </div>
      )}
      <div
        className={`${styles.repairPlanContentsArea} ${
          alignsVertical ? styles.alignsVertical : ""
        }`}
      >
        <RepairPlanContent
          href="https://note.com/nen_studio"
          imgSrc="/images/content_detail_1.png"
          SvgElm={OneSvg}
          title="Prologue of AI ［N］"
          isMobile={isMobile}
        />
        <RepairPlanContent
          href="https://twitter.com/humanoid_N"
          imgSrc="/images/content_detail_2.png"
          SvgElm={TwoSvg}
          title="Diary of AI ［N］"
          isMobile={isMobile}
        />
        <RepairPlanContent
          href="#"
          imgSrc="/images/content_detail_3.png"
          SvgElm={ThreeSvg}
          title="Coming Soon"
          disabled={true}
          isMobile={isMobile}
        />
        {!alignsVertical && (
          <div className="w-[652px]">
            <Image
              src="/images/repair-plan-bottom-right.svg"
              alt=""
              width={652}
              height={104.48}
            />
          </div>
        )}
      </div>
      <div className="my-[40px] w-[110px]">
        <NormalAnimatedBtn onClick={back} extraClass="py-[12px] px-[27.5px]">
          <div className="w-[16px] h-[16px] flex justify-center items-center mr-[4px]">
            <LeftArrowSvg />
          </div>
          <span>BACK</span>
        </NormalAnimatedBtn>
      </div>
    </SubPageLayout>
  );
};

const RepairPlanContent = ({
  href,
  imgSrc,
  SvgElm,
  title,
  disabled = false,
  isMobile,
}: {
  href: string;
  imgSrc: string;
  SvgElm: () => JSX.Element;
  title: string;
  disabled?: boolean;
  isMobile: boolean;
}) => {
  const Wrapper = ({ children }: { children: ReactNode }) => {
    return disabled ? (
      <div className={`${styles.repairPlanContent} ${styles.disabled}`}>
        {children}
      </div>
    ) : (
      <a
        href={href}
        className={styles.repairPlanContent}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  };

  return (
    <Wrapper>
      <div className={styles.imageFrame}>
        <div className={styles.imageWrapper}>
          <Image src={imgSrc} width={340} height={340} alt="" />
        </div>
      </div>
      <div className={styles.imageShadow} />
      <div className={styles.numberWrapper}>
        <SvgElm />
      </div>
      <div
        className={`${styles.titleTextArea} ${disabled ? styles.disabled : ""}`}
      >
        <TitleTextAreaImg isMobile={isMobile} />
        <h2 className={styles.titleText}>{title}</h2>
      </div>
    </Wrapper>
  );
};

const OneSvg = () => (
  <svg
    width="208"
    height="137"
    viewBox="0 0 208 137"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      opacity="0.8"
      d="M62.68 0.0999908L64.0942 -1.31422L63.5084 -1.90001H62.68V0.0999908ZM20.2 0.0999908V-1.90001H19.3716L18.7858 -1.31422L20.2 0.0999908ZM0.400001 19.9L-1.01421 18.4858L-1.6 19.0716V19.9H0.400001ZM0.400001 117.1H-1.6V117.928L-1.01421 118.514L0.400001 117.1ZM20.2 136.9L18.7858 138.314L19.3716 138.9H20.2V136.9ZM62.68 136.9V138.9H63.5084L64.0942 138.314L62.68 136.9ZM82.48 117.1L83.8942 118.514L84.48 117.928V117.1H82.48ZM82.48 19.9H84.48V19.0716L83.8942 18.4858L82.48 19.9ZM69.16 32.5L70.5742 33.9142L71.16 33.3284V32.5H69.16ZM13.72 87.94H11.72V92.7684L15.1342 89.3542L13.72 87.94ZM13.72 24.94L12.3058 23.5258L11.72 24.1116V24.94H13.72ZM26.32 12.34V10.34H25.4916L24.9058 10.9258L26.32 12.34ZM56.56 12.34L57.9742 10.9258L57.3884 10.34H56.56V12.34ZM69.16 24.94H71.16V24.1116L70.5742 23.5258L69.16 24.94ZM69.16 112.06L70.5742 113.474L71.16 112.888V112.06H69.16ZM56.56 124.66V126.66H57.3884L57.9742 126.074L56.56 124.66ZM26.32 124.66L24.9058 126.074L25.4916 126.66H26.32V124.66ZM13.72 112.06H11.72V112.888L12.3058 113.474L13.72 112.06ZM13.72 104.5L12.3058 103.086L11.72 103.672V104.5H13.72ZM69.16 49.06H71.16V44.2316L67.7458 47.6458L69.16 49.06ZM62.68 -1.90001H20.2V2.09999H62.68V-1.90001ZM18.7858 -1.31422L-1.01421 18.4858L1.81421 21.3142L21.6142 1.5142L18.7858 -1.31422ZM-1.6 19.9V117.1H2.4V19.9H-1.6ZM-1.01421 118.514L18.7858 138.314L21.6142 135.486L1.81421 115.686L-1.01421 118.514ZM20.2 138.9H62.68V134.9H20.2V138.9ZM64.0942 138.314L83.8942 118.514L81.0658 115.686L61.2658 135.486L64.0942 138.314ZM84.48 117.1V19.9H80.48V117.1H84.48ZM83.8942 18.4858L64.0942 -1.31422L61.2658 1.5142L81.0658 21.3142L83.8942 18.4858ZM67.7458 31.0858L12.3058 86.5258L15.1342 89.3542L70.5742 33.9142L67.7458 31.0858ZM15.72 87.94V24.94H11.72V87.94H15.72ZM15.1342 26.3542L27.7342 13.7542L24.9058 10.9258L12.3058 23.5258L15.1342 26.3542ZM26.32 14.34H56.56V10.34H26.32V14.34ZM55.1458 13.7542L67.7458 26.3542L70.5742 23.5258L57.9742 10.9258L55.1458 13.7542ZM67.16 24.94V32.5H71.16V24.94H67.16ZM67.7458 110.646L55.1458 123.246L57.9742 126.074L70.5742 113.474L67.7458 110.646ZM56.56 122.66H26.32V126.66H56.56V122.66ZM27.7342 123.246L15.1342 110.646L12.3058 113.474L24.9058 126.074L27.7342 123.246ZM15.72 112.06V104.5H11.72V112.06H15.72ZM15.1342 105.914L70.5742 50.4742L67.7458 47.6458L12.3058 103.086L15.1342 105.914ZM67.16 49.06V112.06H71.16V49.06H67.16ZM122.658 136H120.658V138H122.658V136ZM207.438 136V138H209.438V136H207.438ZM207.438 123.76H209.438V121.76H207.438V123.76ZM173.778 123.76H171.778V125.76H173.778V123.76ZM173.778 0.999989H175.778V-1.00001H173.778V0.999989ZM124.818 0.999989V-1.00001H122.818V0.999989H124.818ZM124.818 13.24H122.818V15.24H124.818V13.24ZM160.458 13.24H162.458V11.24H160.458V13.24ZM160.458 123.76V125.76H162.458V123.76H160.458ZM122.658 123.76V121.76H120.658V123.76H122.658ZM122.658 138H207.438V134H122.658V138ZM209.438 136V123.76H205.438V136H209.438ZM207.438 121.76H173.778V125.76H207.438V121.76ZM175.778 123.76V0.999989H171.778V123.76H175.778ZM173.778 -1.00001H124.818V2.99999H173.778V-1.00001ZM122.818 0.999989V13.24H126.818V0.999989H122.818ZM124.818 15.24H160.458V11.24H124.818V15.24ZM158.458 13.24V123.76H162.458V13.24H158.458ZM160.458 121.76H122.658V125.76H160.458V121.76ZM120.658 123.76V136H124.658V123.76H120.658Z"
      fill="white"
    />
  </svg>
);

const TwoSvg = () => (
  <svg
    width="201"
    height="137"
    viewBox="0 0 201 137"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      opacity="0.8"
      d="M62.68 0.0999908L64.0942 -1.31422L63.5084 -1.90001H62.68V0.0999908ZM20.2 0.0999908V-1.90001H19.3716L18.7858 -1.31422L20.2 0.0999908ZM0.400001 19.9L-1.01421 18.4858L-1.6 19.0716V19.9H0.400001ZM0.400001 117.1H-1.6V117.928L-1.01421 118.514L0.400001 117.1ZM20.2 136.9L18.7858 138.314L19.3716 138.9H20.2V136.9ZM62.68 136.9V138.9H63.5084L64.0942 138.314L62.68 136.9ZM82.48 117.1L83.8942 118.514L84.48 117.928V117.1H82.48ZM82.48 19.9H84.48V19.0716L83.8942 18.4858L82.48 19.9ZM69.16 32.5L70.5742 33.9142L71.16 33.3284V32.5H69.16ZM13.72 87.94H11.72V92.7684L15.1342 89.3542L13.72 87.94ZM13.72 24.94L12.3058 23.5258L11.72 24.1116V24.94H13.72ZM26.32 12.34V10.34H25.4916L24.9058 10.9258L26.32 12.34ZM56.56 12.34L57.9742 10.9258L57.3884 10.34H56.56V12.34ZM69.16 24.94H71.16V24.1116L70.5742 23.5258L69.16 24.94ZM69.16 112.06L70.5742 113.474L71.16 112.888V112.06H69.16ZM56.56 124.66V126.66H57.3884L57.9742 126.074L56.56 124.66ZM26.32 124.66L24.9058 126.074L25.4916 126.66H26.32V124.66ZM13.72 112.06H11.72V112.888L12.3058 113.474L13.72 112.06ZM13.72 104.5L12.3058 103.086L11.72 103.672V104.5H13.72ZM69.16 49.06H71.16V44.2316L67.7458 47.6458L69.16 49.06ZM62.68 -1.90001H20.2V2.09999H62.68V-1.90001ZM18.7858 -1.31422L-1.01421 18.4858L1.81421 21.3142L21.6142 1.5142L18.7858 -1.31422ZM-1.6 19.9V117.1H2.4V19.9H-1.6ZM-1.01421 118.514L18.7858 138.314L21.6142 135.486L1.81421 115.686L-1.01421 118.514ZM20.2 138.9H62.68V134.9H20.2V138.9ZM64.0942 138.314L83.8942 118.514L81.0658 115.686L61.2658 135.486L64.0942 138.314ZM84.48 117.1V19.9H80.48V117.1H84.48ZM83.8942 18.4858L64.0942 -1.31422L61.2658 1.5142L81.0658 21.3142L83.8942 18.4858ZM67.7458 31.0858L12.3058 86.5258L15.1342 89.3542L70.5742 33.9142L67.7458 31.0858ZM15.72 87.94V24.94H11.72V87.94H15.72ZM15.1342 26.3542L27.7342 13.7542L24.9058 10.9258L12.3058 23.5258L15.1342 26.3542ZM26.32 14.34H56.56V10.34H26.32V14.34ZM55.1458 13.7542L67.7458 26.3542L70.5742 23.5258L57.9742 10.9258L55.1458 13.7542ZM67.16 24.94V32.5H71.16V24.94H67.16ZM67.7458 110.646L55.1458 123.246L57.9742 126.074L70.5742 113.474L67.7458 110.646ZM56.56 122.66H26.32V126.66H56.56V122.66ZM27.7342 123.246L15.1342 110.646L12.3058 113.474L24.9058 126.074L27.7342 123.246ZM15.72 112.06V104.5H11.72V112.06H15.72ZM15.1342 105.914L70.5742 50.4742L67.7458 47.6458L12.3058 103.086L15.1342 105.914ZM67.16 49.06V112.06H71.16V49.06H67.16ZM120.138 136H118.138V138H120.138V136ZM200.238 136V138H202.238V136H200.238ZM200.238 123.76H202.238V121.76H200.238V123.76ZM133.458 123.76H131.458V125.76H133.458V123.76ZM133.458 112.96L132.044 111.546L131.458 112.132V112.96H133.458ZM198.438 47.98L199.852 49.3942L200.438 48.8084V47.98H198.438ZM198.438 20.8H200.438V19.9716L199.852 19.3858L198.438 20.8ZM178.638 0.999989L180.052 -0.414224L179.466 -1.00001H178.638V0.999989ZM123.018 0.999989V-1.00001H121.018V0.999989H123.018ZM123.018 13.24H121.018V15.24H123.018V13.24ZM172.518 13.24L173.932 11.8258L173.346 11.24H172.518V13.24ZM185.118 25.84H187.118V25.0116L186.532 24.4258L185.118 25.84ZM185.118 42.94L186.532 44.3542L187.118 43.7684V42.94H185.118ZM120.138 107.92L118.724 106.506L118.138 107.092V107.92H120.138ZM120.138 138H200.238V134H120.138V138ZM202.238 136V123.76H198.238V136H202.238ZM200.238 121.76H133.458V125.76H200.238V121.76ZM135.458 123.76V112.96H131.458V123.76H135.458ZM134.872 114.374L199.852 49.3942L197.024 46.5658L132.044 111.546L134.872 114.374ZM200.438 47.98V20.8H196.438V47.98H200.438ZM199.852 19.3858L180.052 -0.414224L177.224 2.4142L197.024 22.2142L199.852 19.3858ZM178.638 -1.00001H123.018V2.99999H178.638V-1.00001ZM121.018 0.999989V13.24H125.018V0.999989H121.018ZM123.018 15.24H172.518V11.24H123.018V15.24ZM171.104 14.6542L183.704 27.2542L186.532 24.4258L173.932 11.8258L171.104 14.6542ZM183.118 25.84V42.94H187.118V25.84H183.118ZM183.704 41.5258L118.724 106.506L121.552 109.334L186.532 44.3542L183.704 41.5258ZM118.138 107.92V136H122.138V107.92H118.138Z"
      fill="white"
    />
  </svg>
);

const ThreeSvg = () => (
  <svg
    width="197"
    height="137"
    viewBox="0 0 197 137"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      opacity="0.8"
      d="M62.68 0.0999908L64.0942 -1.31422L63.5084 -1.90001H62.68V0.0999908ZM20.2 0.0999908V-1.90001H19.3716L18.7858 -1.31422L20.2 0.0999908ZM0.400001 19.9L-1.01421 18.4858L-1.6 19.0716V19.9H0.400001ZM0.400001 117.1H-1.6V117.928L-1.01421 118.514L0.400001 117.1ZM20.2 136.9L18.7858 138.314L19.3716 138.9H20.2V136.9ZM62.68 136.9V138.9H63.5084L64.0942 138.314L62.68 136.9ZM82.48 117.1L83.8942 118.514L84.48 117.928V117.1H82.48ZM82.48 19.9H84.48V19.0716L83.8942 18.4858L82.48 19.9ZM69.16 32.5L70.5742 33.9142L71.16 33.3284V32.5H69.16ZM13.72 87.94H11.72V92.7684L15.1342 89.3542L13.72 87.94ZM13.72 24.94L12.3058 23.5258L11.72 24.1116V24.94H13.72ZM26.32 12.34V10.34H25.4916L24.9058 10.9258L26.32 12.34ZM56.56 12.34L57.9742 10.9258L57.3884 10.34H56.56V12.34ZM69.16 24.94H71.16V24.1116L70.5742 23.5258L69.16 24.94ZM69.16 112.06L70.5742 113.474L71.16 112.888V112.06H69.16ZM56.56 124.66V126.66H57.3884L57.9742 126.074L56.56 124.66ZM26.32 124.66L24.9058 126.074L25.4916 126.66H26.32V124.66ZM13.72 112.06H11.72V112.888L12.3058 113.474L13.72 112.06ZM13.72 104.5L12.3058 103.086L11.72 103.672V104.5H13.72ZM69.16 49.06H71.16V44.2316L67.7458 47.6458L69.16 49.06ZM62.68 -1.90001H20.2V2.09999H62.68V-1.90001ZM18.7858 -1.31422L-1.01421 18.4858L1.81421 21.3142L21.6142 1.5142L18.7858 -1.31422ZM-1.6 19.9V117.1H2.4V19.9H-1.6ZM-1.01421 118.514L18.7858 138.314L21.6142 135.486L1.81421 115.686L-1.01421 118.514ZM20.2 138.9H62.68V134.9H20.2V138.9ZM64.0942 138.314L83.8942 118.514L81.0658 115.686L61.2658 135.486L64.0942 138.314ZM84.48 117.1V19.9H80.48V117.1H84.48ZM83.8942 18.4858L64.0942 -1.31422L61.2658 1.5142L81.0658 21.3142L83.8942 18.4858ZM67.7458 31.0858L12.3058 86.5258L15.1342 89.3542L70.5742 33.9142L67.7458 31.0858ZM15.72 87.94V24.94H11.72V87.94H15.72ZM15.1342 26.3542L27.7342 13.7542L24.9058 10.9258L12.3058 23.5258L15.1342 26.3542ZM26.32 14.34H56.56V10.34H26.32V14.34ZM55.1458 13.7542L67.7458 26.3542L70.5742 23.5258L57.9742 10.9258L55.1458 13.7542ZM67.16 24.94V32.5H71.16V24.94H67.16ZM67.7458 110.646L55.1458 123.246L57.9742 126.074L70.5742 113.474L67.7458 110.646ZM56.56 122.66H26.32V126.66H56.56V122.66ZM27.7342 123.246L15.1342 110.646L12.3058 113.474L24.9058 126.074L27.7342 123.246ZM15.72 112.06V104.5H11.72V112.06H15.72ZM15.1342 105.914L70.5742 50.4742L67.7458 47.6458L12.3058 103.086L15.1342 105.914ZM67.16 49.06V112.06H71.16V49.06H67.16ZM117.438 136H115.438V138H117.438V136ZM176.658 136V138H177.486L178.072 137.414L176.658 136ZM196.458 116.2L197.872 117.614L198.458 117.028V116.2H196.458ZM196.458 76.6H198.458V75.7716L197.872 75.1858L196.458 76.6ZM176.658 56.8L178.072 55.3858L177.486 54.8H176.658V56.8ZM152.898 56.8H150.898V58.8H152.898V56.8ZM152.898 56.08L151.484 54.6658L150.898 55.2516V56.08H152.898ZM195.558 13.42L196.972 14.8342L197.558 14.2484V13.42H195.558ZM195.558 0.999989H197.558V-1.00001H195.558V0.999989ZM117.438 0.999989V-1.00001H115.438V0.999989H117.438ZM117.438 13.24H115.438V15.24H117.438V13.24ZM178.098 13.24H180.098V11.24H178.098V13.24ZM178.098 13.96L179.512 15.3742L180.098 14.7884V13.96H178.098ZM135.258 56.8L133.844 55.3858L133.258 55.9716V56.8H135.258ZM135.258 69.04H133.258V71.04H135.258V69.04ZM170.538 69.04L171.952 67.6258L171.366 67.04H170.538V69.04ZM183.138 81.64H185.138V80.8116L184.552 80.2258L183.138 81.64ZM183.138 111.16L184.552 112.574L185.138 111.988V111.16H183.138ZM170.538 123.76V125.76H171.366L171.952 125.174L170.538 123.76ZM117.438 123.76V121.76H115.438V123.76H117.438ZM117.438 138H176.658V134H117.438V138ZM178.072 137.414L197.872 117.614L195.044 114.786L175.244 134.586L178.072 137.414ZM198.458 116.2V76.6H194.458V116.2H198.458ZM197.872 75.1858L178.072 55.3858L175.244 58.2142L195.044 78.0142L197.872 75.1858ZM176.658 54.8H152.898V58.8H176.658V54.8ZM154.898 56.8V56.08H150.898V56.8H154.898ZM154.312 57.4942L196.972 14.8342L194.144 12.0058L151.484 54.6658L154.312 57.4942ZM197.558 13.42V0.999989H193.558V13.42H197.558ZM195.558 -1.00001H117.438V2.99999H195.558V-1.00001ZM115.438 0.999989V13.24H119.438V0.999989H115.438ZM117.438 15.24H178.098V11.24H117.438V15.24ZM176.098 13.24V13.96H180.098V13.24H176.098ZM176.684 12.5458L133.844 55.3858L136.672 58.2142L179.512 15.3742L176.684 12.5458ZM133.258 56.8V69.04H137.258V56.8H133.258ZM135.258 71.04H170.538V67.04H135.258V71.04ZM169.124 70.4542L181.724 83.0542L184.552 80.2258L171.952 67.6258L169.124 70.4542ZM181.138 81.64V111.16H185.138V81.64H181.138ZM181.724 109.746L169.124 122.346L171.952 125.174L184.552 112.574L181.724 109.746ZM170.538 121.76H117.438V125.76H170.538V121.76ZM115.438 123.76V136H119.438V123.76H115.438Z"
      fill="#A6A6A6"
    />
  </svg>
);

const TitleTextAreaImg = ({ isMobile }: { isMobile?: boolean }) => (
  <div className={styles.titleTextAreaImg}>
    <div className={styles.upperLeft}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0 0V18L18 0H0Z" fill="#00ACB3" />
      </svg>
    </div>
    <div className={styles.mainArea}>
      {isMobile ? (
        <svg
          width="258"
          height="64"
          viewBox="0 0 258 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.9"
            d="M258 0H32L0 32V63.84L226 64L258 32V0Z"
            fill="white"
          />
        </svg>
      ) : (
        <svg
          width="448"
          height="66"
          viewBox="0 0 448 66"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M448 0H32L0 32V65.84L416 66L448 34V0Z" fill="white" />
        </svg>
      )}
    </div>
    <div className={styles.bottomRight}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M18 18V0L0 18H18Z" fill="#00ACB3" />
      </svg>
    </div>
  </div>
);

export default RepairPlan;
